// SalarySlipView — Zoho-Payroll-style A4 slip.
// Shared between the admin slip preview and the personal (self) view so the
// exported PDF is identical whether generated by the employee or admin.
//
// Props:
//   slip        — selectedSlip row (basic_salary, hra, conveyance, other_allowances,
//                 pf_deduction, esi_deduction, tds_deduction, absent_deduction,
//                 late_deduction, overtime_amount, present_days, absent_days,
//                 working_days, month, year, net_payable, ...)
//   user        — { name, employee_id, date_of_joining, designation }
//   business    — { name, city, logo_url, gstin, ... } from billing_settings
//   ytdSlips    — optional array of prior slips in the same fiscal/calendar year;
//                 used to compute YTD columns. Falls back to "—" if not provided.
//
// The whole thing renders inside a fixed-width A4 canvas (794px @ 96 DPI) so
// html2pdf.js captures it as a proper A4 page.

import React from "react";

const A4_WIDTH = 794;

const MONTH_LABELS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function fmtINR(n) {
  const v = Number(n) || 0;
  return "₹" + v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDateDMY(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  return `${dd}/${mm}/${yy}`;
}

// Indian-English amount-in-words for integer INR
function inrInWords(n) {
  n = Math.round(Number(n) || 0);
  if (n === 0) return "Indian Rupee Zero Only";
  const ones = ["","One","Two","Three","Four","Five","Six","Seven","Eight","Nine","Ten","Eleven","Twelve","Thirteen","Fourteen","Fifteen","Sixteen","Seventeen","Eighteen","Nineteen"];
  const tens = ["","","Twenty","Thirty","Forty","Fifty","Sixty","Seventy","Eighty","Ninety"];
  const two = (x) => x < 20 ? ones[x] : tens[Math.floor(x/10)] + (x%10 ? " " + ones[x%10] : "");
  const three = (x) => x >= 100 ? ones[Math.floor(x/100)] + " Hundred" + (x%100 ? " " + two(x%100) : "") : two(x);
  const cr = Math.floor(n/1e7); n = n % 1e7;
  const lk = Math.floor(n/1e5); n = n % 1e5;
  const th = Math.floor(n/1e3); n = n % 1e3;
  const hu = n;
  let s = "";
  if (cr) s += three(cr) + " Crore ";
  if (lk) s += two(lk) + " Lakh ";
  if (th) s += two(th) + " Thousand ";
  if (hu) s += three(hu);
  return ("Indian Rupee " + s.trim() + " Only").replace(/\s+/g, " ");
}

// Build a lookup {basic:sum, hra:sum, …} across prior slips in same year, sum + current slip.
function computeYtd(currentSlip, ytdSlips) {
  const zero = { basic:0, hra:0, conveyance:0, other_allowances:0, overtime_amount:0,
                 pf:0, esi:0, tds:0, absent:0, late:0, gross:0, deductions:0 };
  if (!Array.isArray(ytdSlips) || ytdSlips.length === 0) return null;
  const acc = { ...zero };
  const allSlips = [...ytdSlips, currentSlip].filter(Boolean);
  for (const s of allSlips) {
    acc.basic            += Number(s.basic_salary)      || 0;
    acc.hra              += Number(s.hra)               || 0;
    acc.conveyance       += Number(s.conveyance)        || 0;
    acc.other_allowances += Number(s.other_allowances)  || 0;
    acc.overtime_amount  += Number(s.overtime_amount)   || 0;
    acc.pf               += Number(s.pf_deduction)      || 0;
    acc.esi              += Number(s.esi_deduction)     || 0;
    acc.tds              += Number(s.tds_deduction)     || 0;
    acc.absent           += Number(s.absent_deduction)  || 0;
    acc.late             += Number(s.late_deduction)    || 0;
    acc.gross            += (Number(s.gross_salary)     || 0) + (Number(s.overtime_amount) || 0);
    acc.deductions       += Number(s.total_deductions)  || 0;
  }
  return acc;
}

// ─── Component ─────────────────────────────────────────────────────────────

export default function SalarySlipView({ slip, user = {}, business = {}, ytdSlips = null }) {
  if (!slip) return null;
  const monthLabel = MONTH_LABELS[(Number(slip.month) || 1) - 1] || String(slip.month);
  const monthYear = `${monthLabel} ${slip.year}`;
  const paidDays = Math.max(0, (Number(slip.working_days) || 0) - (Number(slip.absent_days) || 0));
  const lopDays = Number(slip.absent_days) || 0;
  const grossEarnings = (Number(slip.gross_salary) || 0) + (Number(slip.overtime_amount) || 0);
  const totalDeductions = Number(slip.total_deductions) || 0;
  const netPay = Number(slip.net_payable) || (grossEarnings - totalDeductions);
  const ytd = computeYtd(slip, ytdSlips);
  const ytdCell = (key) => ytd ? fmtINR(ytd[key]) : "—";
  const payPeriod = monthYear;
  const payDate = slip.paid_at || slip.payment_date || slip.updated_at || "";

  // Earnings rows — hide rows that are zero unless they're the essential four
  const earnings = [
    { label: "Basic",                 amount: slip.basic_salary,      ytdKey: "basic",            always: true },
    { label: "House Rent Allowance",  amount: slip.hra,               ytdKey: "hra",              always: true },
    { label: "Conveyance Allowance",  amount: slip.conveyance,        ytdKey: "conveyance",       always: true },
    { label: "Other Allowances",      amount: slip.other_allowances,  ytdKey: "other_allowances", always: false },
    { label: "Overtime",              amount: slip.overtime_amount,   ytdKey: "overtime_amount",  always: false },
  ].filter(r => r.always || (Number(r.amount) || 0) > 0);

  const deductions = [
    { label: "EPF Contribution",   amount: slip.pf_deduction,     ytdKey: "pf",         always: true },
    { label: "ESI Contribution",   amount: slip.esi_deduction,    ytdKey: "esi",        always: false },
    { label: "TDS",                amount: slip.tds_deduction,    ytdKey: "tds",        always: false },
    { label: "Absent Deduction",   amount: slip.absent_deduction, ytdKey: "absent",     always: false },
    { label: "Late Deduction",     amount: slip.late_deduction,   ytdKey: "late",       always: false },
  ].filter(r => r.always || (Number(r.amount) || 0) > 0);

  return (
    <div style={{
      width: A4_WIDTH,
      background: "#fff",
      color: "#1F2937",
      fontFamily: "'Inter', 'Plus Jakarta Sans', system-ui, sans-serif",
      fontSize: 12.5,
      lineHeight: 1.55,
      padding: 40,
      boxSizing: "border-box",
    }}>
      {/* ─── Header ─── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", paddingBottom: 20, borderBottom: "1px solid #E5E7EB" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          {business.logo_url ? (
            <img src={business.logo_url} alt="" crossOrigin="anonymous" style={{ height: 48, objectFit: "contain" }} />
          ) : (
            <div style={{
              width: 48, height: 48, borderRadius: 10,
              background: "linear-gradient(135deg,#818CF8,#6366F1)",
              color: "#fff", fontWeight: 800, fontSize: 20,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>{(business.name || "D").charAt(0).toUpperCase()}</div>
          )}
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: "-.3px", color: "#111827" }}>{business.name || "Company"}</div>
            {(() => {
              const cityLine = [business.city, business.state].filter(Boolean).join(", ") + (business.pincode ? ` - ${business.pincode}` : "");
              const addr = [business.address, cityLine].filter(Boolean).join(", ");
              return (
                <>
                  {addr && <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2, maxWidth: 380 }}>{addr}</div>}
                  {business.phone && <div style={{ fontSize: 12, color: "#6B7280", marginTop: 1 }}>Phone: {business.phone}</div>}
                </>
              );
            })()}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 11.5, color: "#6B7280" }}>Payslip For the Month</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: "#111827", marginTop: 2 }}>{monthYear}</div>
        </div>
      </div>

      {/* ─── Employee Summary ─── */}
      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 24, marginTop: 24 }}>
        <div style={{ borderRight: "1px dashed #D1D5DB", paddingRight: 20 }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: "#6B7280", letterSpacing: ".8px", textTransform: "uppercase", marginBottom: 10 }}>Employee Summary</div>
          <MetaRow label="Employee Name" value={user.name || slip.user_name || "—"} />
          <MetaRow label="Designation" value={user.designation || "—"} />
          <MetaRow label="Employee ID" value={user.employee_id || "—"} />
          <MetaRow label="Date of Joining" value={user.date_of_joining ? fmtDateDMY(user.date_of_joining) : "—"} />
          <MetaRow label="Pay Period" value={payPeriod} />
          <MetaRow label="Pay Date" value={payDate ? fmtDateDMY(payDate) : "—"} />
        </div>

        <div style={{
          border: "1px solid #D1FAE5",
          borderLeft: "4px solid #10B981",
          background: "#F0FDF4",
          borderRadius: 10, padding: 16,
        }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: "#111827", letterSpacing: "-.4px" }}>{fmtINR(netPay)}</div>
          <div style={{ fontSize: 11, color: "#059669", fontWeight: 600, marginTop: 2 }}>Employee Net Pay</div>
          <div style={{ height: 1, background: "#D1FAE5", margin: "12px 0" }} />
          <MetaRow label="Paid Days" value={String(paidDays)} tight />
          <MetaRow label="LOP Days" value={String(lopDays)} tight />
        </div>
      </div>

      {/* ─── PF A/C + UAN ─── */}
      {(slip.pf_account || slip.uan) && (
        <div style={{ marginTop: 18, padding: "12px 0", borderTop: "1px dashed #D1D5DB", borderBottom: "1px dashed #D1D5DB", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <MetaRow label="PF A/C Number" value={slip.pf_account || "—"} tight />
          <MetaRow label="UAN" value={slip.uan || "—"} tight />
        </div>
      )}

      {/* ─── Earnings / Deductions ─── */}
      <div style={{ marginTop: 22, border: "1px solid #E5E7EB", borderRadius: 12, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
          {/* Earnings */}
          <div style={{ borderRight: "1px solid #E5E7EB" }}>
            <TableHead cols={["Earnings", "Amount", "YTD"]} />
            {earnings.map((r, i) => (
              <TableRow key={i} label={r.label} amount={fmtINR(r.amount)} ytd={ytdCell(r.ytdKey)} />
            ))}
            <TotalRow label="Gross Earnings" amount={fmtINR(grossEarnings)} ytd={ytdCell("gross")} />
          </div>

          {/* Deductions */}
          <div>
            <TableHead cols={["Deductions", "Amount", "YTD"]} />
            {deductions.map((r, i) => (
              <TableRow key={i} label={r.label} amount={fmtINR(r.amount)} ytd={ytdCell(r.ytdKey)} />
            ))}
            <TotalRow label="Total Deductions" amount={fmtINR(totalDeductions)} ytd={ytdCell("deductions")} />
          </div>
        </div>
      </div>

      {/* ─── Total Net Payable strip ─── */}
      <div style={{ marginTop: 20, border: "1px solid #D1FAE5", borderRadius: 12, padding: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#111827", letterSpacing: ".4px", textTransform: "uppercase" }}>Total Net Payable</div>
          <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>Gross Earnings − Total Deductions</div>
        </div>
        <div style={{ background: "#F0FDF4", padding: "8px 16px", borderRadius: 8, fontSize: 22, fontWeight: 800, color: "#065F46" }}>
          {fmtINR(netPay)}
        </div>
      </div>

      {/* ─── Amount in words ─── */}
      <div style={{ marginTop: 12, textAlign: "right", fontSize: 12, color: "#374151" }}>
        <span style={{ color: "#6B7280" }}>Amount In Words : </span>
        <b style={{ color: "#111827" }}>{inrInWords(netPay)}</b>
      </div>

      {/* ─── Footer ─── */}
      <div style={{ marginTop: 26, paddingTop: 12, borderTop: "1px solid #E5E7EB", textAlign: "center", fontSize: 10.5, color: "#9CA3AF" }}>
        — This document has been automatically generated by {business.name || "Dnexus CRM"}; therefore, a signature is not required. —
      </div>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function MetaRow({ label, value, tight }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "130px 12px 1fr", padding: tight ? "3px 0" : "6px 0", fontSize: 12.5 }}>
      <span style={{ color: "#6B7280" }}>{label}</span>
      <span style={{ color: "#6B7280" }}>:</span>
      <span style={{ color: "#111827", fontWeight: 500 }}>{value}</span>
    </div>
  );
}

function TableHead({ cols }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr 1fr", background: "#F9FAFB", padding: "10px 14px", borderBottom: "1px solid #E5E7EB", fontSize: 10.5, fontWeight: 700, color: "#6B7280", letterSpacing: ".8px", textTransform: "uppercase" }}>
      <span>{cols[0]}</span>
      <span style={{ textAlign: "right" }}>{cols[1]}</span>
      <span style={{ textAlign: "right" }}>{cols[2]}</span>
    </div>
  );
}

function TableRow({ label, amount, ytd }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr 1fr", padding: "9px 14px", fontSize: 12.5, borderBottom: "1px solid #F3F4F6" }}>
      <span style={{ color: "#374151" }}>{label}</span>
      <span style={{ textAlign: "right", color: "#111827", fontWeight: 600 }}>{amount}</span>
      <span style={{ textAlign: "right", color: "#6B7280" }}>{ytd}</span>
    </div>
  );
}

function TotalRow({ label, amount, ytd }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr 1fr", padding: "10px 14px", fontSize: 12.5, background: "#F9FAFB", fontWeight: 700 }}>
      <span style={{ color: "#111827" }}>{label}</span>
      <span style={{ textAlign: "right", color: "#111827" }}>{amount}</span>
      <span style={{ textAlign: "right", color: "#374151" }}>{ytd}</span>
    </div>
  );
}
